// ==================== CONTACT US PAGE ====================

import { useState } from 'react';
import { motion } from 'framer-motion';
import { useForm, Controller } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { 
  Mail, 
  Phone, 
  MapPin, 
  Clock,
  Send,
  CheckCircle2,
  AlertCircle,
  MessageSquare,
  Building,
  HelpCircle,
  Users
} from 'lucide-react';
import { Card } from '../../components/ui/Card';
import { Badge } from '../../components/ui/Badge';
import { Button } from '../../components/ui/Button';
import { FormInput } from '../../components/ui/FormInput';
import { Textarea } from '../../components/ui/Textarea';
import { Select, type SelectOption } from '../../components/ui/Select';
import { Checkbox } from '../../components/ui/Checkbox';
import { APP_CONSTANTS } from '../../constants';
import { apiService } from '../../services/api';
import { contactFormSchema, type ContactFormData } from '../../utils/form-validations';
import {
  fadeInUp,
  staggerContainer,
  scaleIn,
  hoverScale,
  tapScale
} from '../../utils/animations';

// Subject Options
const subjectOptions: SelectOption[] = [
  { value: 'general', label: 'General Inquiry', icon: <HelpCircle size={16} /> },
  { value: 'sales', label: 'Sales & Pricing', icon: <Building size={16} /> },
  { value: 'support', label: 'Technical Support', icon: <MessageSquare size={16} /> },
  { value: 'partnership', label: 'Partnership', icon: <Users size={16} /> },
];

// Contact Info
const contactInfo = [
  {
    icon: Mail,
    label: 'Email',
    value: APP_CONSTANTS.CONTACT_EMAIL,
    href: `mailto:${APP_CONSTANTS.CONTACT_EMAIL}`,
  },
  {
    icon: Phone,
    label: 'Phone',
    value: APP_CONSTANTS.CONTACT_PHONE,
    href: `tel:${APP_CONSTANTS.CONTACT_PHONE}`,
  },
  {
    icon: MapPin,
    label: 'Address',
    value: '18 Zaman Park, Lane 2, Canal Road, Lahore, Pakistan',
    href: null,
  },
  {
    icon: Clock,
    label: 'Business Hours',
    value: 'Mon - Fri: 9AM - 6PM PST',
    href: null,
  },
];

const ContactPage = () => {
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [formError, setFormError] = useState<string | null>(null);

  const {
    register,
    handleSubmit,
    control,
    reset,
    formState: { errors },
  } = useForm<ContactFormData>({
    resolver: zodResolver(contactFormSchema),
    defaultValues: {
      firstName: '',
      lastName: '',
      email: '',
      phone: '',
      company: '',
      subject: '',
      message: '',
      agreeToTerms: false,
      website: '',
    },
  });

  const onSubmit = async (data: ContactFormData) => {
    setIsSubmitting(true);
    setFormError(null);

    const response = await apiService.post('/public/contact', {
      firstName: data.firstName,
      lastName: data.lastName,
      email: data.email,
      phone: data.phone || undefined,
      company: data.company || undefined,
      subject: data.subject,
      message: data.message,
      website: data.website || undefined,
    });

    setIsSubmitting(false);

    if (response.success) {
      setIsSubmitted(true);
      reset();
      setTimeout(() => setIsSubmitted(false), 5000);
    } else {
      const msg = response.message || 'Something went wrong. Please try again later.';
      setFormError(
        msg.includes('Too Many Requests')
          ? 'You have submitted too many requests. Please wait a few minutes and try again.'
          : msg,
      );
      setTimeout(() => setFormError(null), 8000);
    }
  };

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      transition={{ duration: 0.5 }}
    >
      {/* Hero Section */}
      <section className="relative py-20 lg:py-28 overflow-hidden">
        <div className="absolute inset-0 bg-linear-to-b from-(--color-midnight-navy) via-(--color-dark-navy) to-(--color-midnight-navy)" />
        <motion.div 
          className="absolute top-20 left-1/4 w-[400px] h-[400px] bg-(--color-signal-orange)/8 rounded-full blur-3xl"
          animate={{ scale: [1, 1.2, 1], opacity: [0.3, 0.5, 0.3] }}
          transition={{ duration: 8, repeat: Infinity }}
        />

        <div className="container-custom relative z-10">
          <motion.div
            className="max-w-3xl mx-auto text-center"
            initial="hidden"
            animate="visible"
            variants={staggerContainer}
          >
            <motion.div variants={fadeInUp}>
              <Badge variant="orange" className="mb-6">Contact Us</Badge>
            </motion.div>
            
            <motion.h1
              variants={fadeInUp}
              className="text-4xl md:text-5xl lg:text-6xl font-heading font-bold text-white leading-tight mb-6"
            >
              Get in
              <span className="text-gradient"> Touch</span>
            </motion.h1>
            
            <motion.p
              variants={fadeInUp}
              className="text-lg md:text-xl text-(--color-soft-gray) leading-relaxed"
            >
              Have questions? We'd love to hear from you. Send us a message and we'll respond as soon as possible.
            </motion.p>
          </motion.div>
        </div>
      </section>

      {/* Contact Section */}
      <section className="py-20 lg:py-28">
        <div className="container-custom">
          <div className="grid lg:grid-cols-3 gap-12">
            {/* Contact Info */}
            <motion.div
              className="lg:col-span-1"
              initial="hidden"
              whileInView="visible"
              viewport={{ once: true }}
              variants={staggerContainer}
            >
              <motion.div variants={fadeInUp} className="mb-8">
                <h2 className="text-2xl font-heading font-bold text-white mb-4">
                  Contact Information
                </h2>
                <p className="text-(--color-muted-gray)">
                  Fill out the form and our team will get back to you within 24 hours.
                </p>
              </motion.div>

              <motion.div variants={staggerContainer} className="space-y-6">
                {contactInfo.map((item, index) => (
                  <motion.div
                    key={index}
                    variants={fadeInUp}
                    className="flex items-start gap-4"
                  >
                    <div className="w-12 h-12 rounded-xl bg-(--color-orange-glow) flex items-center justify-center shrink-0">
                      <item.icon size={20} className="text-(--color-signal-orange)" />
                    </div>
                    <div>
                      <p className="text-sm text-(--color-muted-gray) mb-1">{item.label}</p>
                      {item.href ? (
                        <a
                          href={item.href}
                          className="text-white hover:text-(--color-signal-orange) transition-colors"
                        >
                          {item.value}
                        </a>
                      ) : (
                        <p className="text-white">{item.value}</p>
                      )}
                    </div>
                  </motion.div>
                ))}
              </motion.div>

              {/* Map Preview */}
              <motion.div
                variants={fadeInUp}
                className="mt-8 rounded-xl overflow-hidden border border-(--color-border-gray) h-48 bg-(--color-charcoal-black)"
              >
                <iframe
                  title="Office Location"
                  src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d3401.5!2d74.3!3d31.52!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x0%3A0x0!2zMzHCsDMxJzEyLjAiTiA3NMKwMTgnMDAuMCJF!5e0!3m2!1sen!2spk!4v1700000000000!5m2!1sen!2spk&q=18+Zaman+Park,+Canal+Road,+Lahore,+Pakistan"
                  width="100%"
                  height="100%"
                  style={{ border: 0 }}
                  allowFullScreen
                  loading="lazy"
                  referrerPolicy="no-referrer-when-downgrade"
                />
              </motion.div>
            </motion.div>

            {/* Contact Form */}
            <motion.div
              className="lg:col-span-2"
              initial="hidden"
              whileInView="visible"
              viewport={{ once: true }}
              variants={scaleIn}
            >
              <Card className="p-8">
                {isSubmitted ? (
                  // Success Message
                  <motion.div
                    initial={{ opacity: 0, scale: 0.9 }}
                    animate={{ opacity: 1, scale: 1 }}
                    className="text-center py-12"
                  >
                    <div className="w-20 h-20 mx-auto mb-6 rounded-full bg-(--color-success)/20 flex items-center justify-center">
                      <CheckCircle2 size={40} className="text-(--color-success)" />
                    </div>
                    <h3 className="text-2xl font-heading font-bold text-white mb-2">
                      Message Sent!
                    </h3>
                    <p className="text-(--color-muted-gray)">
                      Thank you for reaching out. We'll get back to you shortly.
                    </p>
                  </motion.div>
                ) : (
                  // Contact Form
                  <form onSubmit={handleSubmit(onSubmit)} className="space-y-6">
                    <div className="grid md:grid-cols-2 gap-6">
                      {/* First Name */}
                      <FormInput
                        label="First Name"
                        placeholder="John"
                        error={errors.firstName?.message}
                        required
                        {...register('firstName')}
                      />

                      {/* Last Name */}
                      <FormInput
                        label="Last Name"
                        placeholder="Doe"
                        error={errors.lastName?.message}
                        required
                        {...register('lastName')}
                      />
                    </div>

                    <div className="grid md:grid-cols-2 gap-6">
                      {/* Email */}
                      <FormInput
                        label="Email"
                        type="email"
                        placeholder="john@example.com"
                        leftIcon={<Mail size={18} />}
                        error={errors.email?.message}
                        required
                        {...register('email')}
                      />

                      {/* Phone */}
                      <FormInput
                        label="Phone"
                        type="tel"
                        placeholder="+1 (555) 000-0000"
                        leftIcon={<Phone size={18} />}
                        error={errors.phone?.message}
                        {...register('phone')}
                      />
                    </div>

                    {/* Company */}
                    <FormInput
                      label="Company"
                      placeholder="Your company name"
                      leftIcon={<Building size={18} />}
                      {...register('company')}
                    />

                    {/* Subject */}
                    <Controller
                      name="subject"
                      control={control}
                      render={({ field }) => (
                        <Select
                          label="Subject"
                          placeholder="Select a subject"
                          options={subjectOptions}
                          value={field.value}
                          onChange={field.onChange}
                          error={errors.subject?.message}
                          required
                          searchable
                        />
                      )}
                    />

                    {/* Message */}
                    <Controller
                      name="message"
                      control={control}
                      render={({ field }) => (
                        <Textarea
                          label="Message"
                          placeholder="Tell us how we can help you..."
                          error={errors.message?.message}
                          showCharCount
                          maxLength={1000}
                          required
                          {...field}
                        />
                      )}
                    />

                    {/* Terms Checkbox */}
                    <Controller
                      name="agreeToTerms"
                      control={control}
                      render={({ field }) => (
                        <Checkbox
                          label="I agree to the Terms of Service and Privacy Policy"
                          checked={field.value}
                          onChange={field.onChange}
                          error={errors.agreeToTerms?.message}
                          required
                        />
                      )}
                    />

                    {/* Honeypot — hidden from humans, bots auto-fill it */}
                    <div aria-hidden="true" style={{ position: 'absolute', left: '-9999px', opacity: 0, height: 0, overflow: 'hidden' }}>
                      <input type="text" tabIndex={-1} autoComplete="off" {...register('website')} />
                    </div>

                    {/* Error Message */}
                    {formError && (
                      <motion.div
                        initial={{ opacity: 0, y: -8 }}
                        animate={{ opacity: 1, y: 0 }}
                        className="flex items-center gap-3 p-4 rounded-xl bg-red-500/10 border border-red-500/30"
                      >
                        <AlertCircle size={20} className="text-red-400 shrink-0" />
                        <p className="text-sm text-red-400">{formError}</p>
                      </motion.div>
                    )}

                    {/* Submit Button */}
                    <motion.div whileHover={hoverScale} whileTap={tapScale}>
                      <Button
                        type="submit"
                        variant="primary"
                        size="lg"
                        fullWidth
                        isLoading={isSubmitting}
                        rightIcon={<Send size={18} />}
                      >
                        {isSubmitting ? 'Sending...' : 'Send Message'}
                      </Button>
                    </motion.div>
                  </form>
                )}
              </Card>
            </motion.div>
          </div>
        </div>
      </section>

      {/* FAQ Section */}
      <section className="py-20 lg:py-28 bg-(--color-dark-navy)">
        <div className="container-custom">
          <motion.div
            className="text-center max-w-2xl mx-auto mb-16"
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            variants={fadeInUp}
          >
            <Badge variant="orange" className="mb-4">FAQ</Badge>
            <h2 className="text-3xl lg:text-4xl font-heading font-bold text-white mb-4">
              Frequently Asked
              <span className="text-gradient"> Questions</span>
            </h2>
            <p className="text-(--color-muted-gray)">
              Quick answers to common questions about AmplifyMsg.
            </p>
          </motion.div>

          <motion.div
            className="grid md:grid-cols-2 gap-6 max-w-4xl mx-auto"
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            variants={staggerContainer}
          >
            {[
              {
                question: 'How quickly can I get started?',
                answer: 'You can sign up and start using AmplifyMsg within minutes. Our guided setup process makes it easy to connect your WhatsApp Business API account.',
              },
              {
                question: 'Do you offer a demo?',
                answer: 'Yes! Contact our sales team to schedule a personalized demo. We\'ll walk you through all features and answer your questions.',
              },
              {
                question: 'What payment methods do you accept?',
                answer: 'We accept all major credit cards, PayPal, and bank transfers for annual plans. Contact us for custom billing arrangements.',
              },
              {
                question: 'Is there a minimum contract period?',
                answer: 'No, all our plans are month-to-month with no long-term commitment required. You can upgrade, downgrade, or cancel anytime.',
              },
            ].map((faq, index) => (
              <motion.div key={index} variants={fadeInUp}>
                <Card hoverable className="h-full">
                  <h3 className="text-lg font-heading font-semibold text-white mb-2">
                    {faq.question}
                  </h3>
                  <p className="text-sm text-(--color-muted-gray) leading-relaxed">
                    {faq.answer}
                  </p>
                </Card>
              </motion.div>
            ))}
          </motion.div>
        </div>
      </section>
    </motion.div>
  );
};

export { ContactPage };

